import {get, post, request} from '../apiClient'
import {ENDPOINTS, HEADERS} from '@/utils/constants/apiEndpoints'
import type {DashboardPageResponse, DashboardPageParams} from '../types'
import type {DataValidationWindowResponse} from '@/components/SEMetrics/helpers/types'

// Define query parameters interface for program list
export interface ProgramListParams {
  page?: number
  size?: number
  search?: string
}

// Define query parameters interface for SE Metrics
export interface SEMetricsParams {
  productDashboardId: string
  month: number
  year: number
  timestamp?: number // Optional timestamp for cache-busting after manual updates
}

// Define query parameters interface for bulk SE Metrics
export interface SEMetricsBulkParams {
  productDashboardIdList?: string // Optional comma-separated list of product dashboard IDs
  month: number
  year: number
  timestamp?: number // Optional timestamp for cache-busting after manual updates
  isVisibleOnSeMetricsPage?: boolean // Optional flag to filter programs visible on SE Metrics page
  visibilityOnSEMetricsPage?: boolean // Optional flag for SE Metrics page visibility
}

export interface ManualFeatureDeliveryPayload {
  productDashboardId: string
  entries: Array<{
    month: number
    year: number
    noOfFeaturesDelivered: number
    noOfContributors: number
  }>
}

export interface ManualFeatureDeliveryTrendsParams {
  productDashboardIdList: string
  fromPeriod: string
  toPeriod: string
}

export interface ManualFeatureDeliveryTrendsRequestBody {
  programIds: string[]
  fromPeriod: string
  toPeriod: string
  aggregatedProgramIds?: Record<string, string[]>
}

export interface ManualFeatureDeliveryTrendPoint {
  period: string
  year?: number
  month?: number
  label: string
  featuresDelivered: number
  contributors: number
  featuresPerContributor?: number
}

export interface ManualFeatureDeliveryProgram {
  productDashboardId: string
  programName: string
}

export interface ManualFeatureDeliverySummary {
  totalFeaturesDelivered: number
  totalContributors: number
  featuresPerContributor: number
}

export interface ManualFeatureDeliveryTrendSeries {
  key: 'featuresDelivered' | 'contributors' | 'featuresPerContributor'
  name: string
  data: number[]
}

export interface ManualFeatureDeliveryProgramTrend {
  productDashboardId?: string
  programId?: string
  key?: string
  programName?: string
  programIds?: string[]
  programs?: ManualFeatureDeliveryProgram[]
  summary?: ManualFeatureDeliverySummary
  monthlyTrend: ManualFeatureDeliveryTrendPoint[]
  series: ManualFeatureDeliveryTrendSeries[]
}

export interface ManualFeatureDeliveryTrendsResponse {
  startPeriod: string
  endPeriod: string
  months: number
  programs?: ManualFeatureDeliveryProgram[]
  summary?: ManualFeatureDeliverySummary
  monthlyTrend: ManualFeatureDeliveryTrendPoint[]
  series: ManualFeatureDeliveryTrendSeries[]
  programMetrics?: ManualFeatureDeliveryProgramTrend[]
  aggregatedProgramMetrics?: Record<string, ManualFeatureDeliveryProgramTrend>
}

// AI Productivity Hierarchical interface moved to aiMetricsService.ts

/**
 * Service to handle dashboard API calls
 */
export const dashboardService = {
  /**
   * Fetch teams list (dashboard page with filters)
   */
  getTeamsList: async (params: DashboardPageParams = {}): Promise<DashboardPageResponse> => {
    try {
      // Set default values for params, but preserve all other params (including cache-busting)
      const queryParams = {
        page: params.page ?? 0,
        size: params.size ?? 10,
        search: params.search ?? '',
        ...params,
      }

      // Using the get function from apiClient
      const response = await get(ENDPOINTS.TEAMS_LIST_PAGE_FILTER, {
        params: queryParams,
      })
      return response.data
    } catch (error) {
      console.error('Error fetching teams list:', error)
      throw error
    }
  },
  /**
   * Fetch all product dashboards using the page/filter endpoint
   */
  getProductDashboardsWithParams: async (
    params: ProgramListParams = {},
  ): Promise<{productDashboards: any[]; total: number}> => {
    try {
      const response = await get(ENDPOINTS.PROGRAMS_LIST_PAGE_FILTER, {params})
      // The API returns { productDashboards: [...], total: number }
      return {
        productDashboards: response.data.productDashboards || [],
        total: response.data.total || 0,
      }
    } catch (error) {
      console.error('Error fetching all product dashboards:', error)
      throw error
    }
  },
  getAllProductDashboards: async (): Promise<any[]> => {
    try {
      const response = await get(ENDPOINTS.PRODUCT_DASHBOARDS_LIST)
      return response.data || []
    } catch (error) {
      console.error('Error fetching all product dashboards:', error)
      throw error
    }
  },
  /**
   * Fetch all programs for dropdown (without pagination)
   * @param noCache - If true, bypasses server cache to get fresh data
   */
  getAllProgramsList: async (noCache = false): Promise<any[]> => {
    try {
      const response = await get(ENDPOINTS.ALL_PROGRAMS_LIST, {
        headers: noCache ? {[HEADERS.NO_CACHE]: 'true'} : {},
      })
      return response.data || []
    } catch (error) {
      console.error('Error fetching all programs list:', error)
      throw error
    }
  },

  /**
   * Fetch all programs with AppSecFlow widget data for ServiceNow navigation.
   * Uses the same /productDashboards/all-info endpoint with isWidgetExport=true
   * and widgetName=AppSecFlow to include widgetData.AppSecFlow configs.
   * Single call replaces multiple per-component fetchAppSecFlowConfigurations calls.
   */
  getAllProgramsWithAppSecFlow: async (): Promise<any[]> => {
    try {
      const response = await get(ENDPOINTS.ALL_PROGRAMS_LIST, {
        params: {isWidgetExport: true, widgetName: 'AppSecFlow'},
      })
      return response.data || []
    } catch (error) {
      console.error('Error fetching programs with AppSecFlow widget data:', error)
      return []
    }
  },

  /**
   * Fetch SE Metrics data for a given productDashboardId, month, and year
   */
  getSEMetrics: async (params: SEMetricsParams): Promise<any> => {
    try {
      // Reduced logging to avoid console spam
      const response = await get(ENDPOINTS.SE_METRICS_V2, {params})
      return response.data
    } catch (error) {
      console.error('Error fetching SE Metrics data:', error)
      throw error
    }
  },
  /**
   * Fetch SE Metrics data for multiple programs in bulk by period
   * @param params - Parameters including optional productDashboardIdList, month, and year
   * @returns Promise with array of SE Metrics data for all programs
   */
  getSEMetricsBulk: async (params: SEMetricsBulkParams): Promise<any[]> => {
    try {
      const response = await get(ENDPOINTS.SE_METRICS_LIST_BY_PERIOD, {
        params,
        headers: {[HEADERS.NO_CACHE]: 'true'},
      })
      return response.data || []
    } catch (error) {
      console.error('Error fetching bulk SE Metrics data:', error)
      throw error
    }
  },
  /**
   * Download SE Metrics report as a file (Excel/CSV) for a given productDashboardId, month, and year
   * Returns a Blob for file download
   */
  getSEMetricsReport: async (params: SEMetricsParams): Promise<Blob> => {
    try {
      // Set responseType to 'blob' to receive file data
      const response = await get(ENDPOINTS.SE_METRICS_REPORT, {
        params,
        responseType: 'blob',
      })
      return response.data as Blob
    } catch (error) {
      console.error('Error fetching SE Metrics report:', error)
      throw error
    }
  },
  getSEMetricsAllReports: async (params: {month: number; year: number}): Promise<Blob> => {
    try {
      const response = await get(ENDPOINTS.SE_METRICS_ALL_REPORTS, {
        params,
        responseType: 'blob',
      })
      return response.data as Blob
    } catch (error) {
      console.error('Error fetching SE Metrics all reports:', error)
      throw error
    }
  },
  getPowerBIReport: async (): Promise<any> => {
    try {
      const response = await get(ENDPOINTS.POWER_BI_REPORT)
      return response.data
    } catch (error) {
      console.error('Error fetching Power BI report:', error)
      throw error
    }
  },
  /**
   * Fetch monthly cost data for all cloud services/programs
   * @param params { services: string[], startDate: string, endDate: string }
   */
  fetchMonthlyKubeCost: async (params: {services: string[]; startDate: string; endDate: string}): Promise<any> => {
    try {
      // Updated to use the new consolidated endpoint
      const response = await post(ENDPOINTS.AZURE_CLOUD_COST_OVERALL, {data: params})
      return response.data
    } catch (error) {
      console.error('Error fetching monthly kube cost:', error)
      throw error
    }
  },
  /**
   * Fetches a dashboard by its ID with no-cache header
   * @param dashboardId - The ID of the dashboard to fetch
   * @returns Promise with the dashboard data
   */
  getDashboard: async (dashboardId: string) => {
    return get(ENDPOINTS.DASHBOARD_BY_ID + dashboardId, {
      headers: {[HEADERS.NO_CACHE]: 'true'},
    })
  },
  /**
   * Fetch data validation window information for SE Metrics
   */
  getDataValidationWindow: async (): Promise<DataValidationWindowResponse> => {
    try {
      const response = await get(ENDPOINTS.SE_METRICS_DATA_VALIDATION_WINDOW)
      return response.data
    } catch (error) {
      console.error('Error fetching data validation window:', error)
      throw error
    }
  },

  /**
   * Manual metrics (Angular parity)
   * - getManualEntry(productDashboardId, period: YYYY-MM)
   * - createManualEntry(payload)
   * - updateManualEntry(payload)
   */
  getManualEntry: async (productDashboardId: string, period: string): Promise<any> => {
    const response = await get(ENDPOINTS.SE_MANUAL_METRICS, {
      params: {productDashboardId, period},
      headers: {[HEADERS.NO_CACHE]: 'true'},
    })
    return response.data
  },
  createManualEntry: async (payload: any): Promise<any> => {
    const response = await post(ENDPOINTS.SE_MANUAL_METRICS, {data: payload})
    return response.data
  },
  updateManualEntry: async (payload: any): Promise<any> => {
    const response = await request(ENDPOINTS.SE_MANUAL_METRICS, {data: payload}, 'PUT' as any)
    return response.data
  },
  createManualFeatureDeliveryEntry: async (payload: ManualFeatureDeliveryPayload): Promise<any> => {
    const response = await post(ENDPOINTS.SE_MANUAL_FEATURE_DELIVERY, {data: payload})
    return response.data
  },
  getManualFeatureDeliveryTrends: async (
    params: ManualFeatureDeliveryTrendsParams,
  ): Promise<ManualFeatureDeliveryTrendsResponse> => {
    const response = await get(ENDPOINTS.SE_MANUAL_FEATURE_DELIVERY_TRENDS, {params})
    return response.data
  },
  getManualFeatureDeliveryTrendsByPrograms: async (
    payload: ManualFeatureDeliveryTrendsRequestBody,
  ): Promise<ManualFeatureDeliveryTrendsResponse> => {
    const response = await post(ENDPOINTS.SE_MANUAL_FEATURE_DELIVERY_TRENDS, {data: payload})
    return response.data
  },
  /**
   * Transition Alert (Edit Status Side Panel)
   * - transitionAlert(productDashboardId, month, year, payload)
   * Angular parity: used for status change/flag for review
   */
  transitionAlert: async (productDashboardId: string, month: number, year: number, payload: any): Promise<any> => {
    try {
      const url = `${ENDPOINTS.TRANSITION_ALERT}/${productDashboardId}`
      const response = await request(
        url,
        {
          data: payload,
          params: {month: month.toString(), year: year.toString()},
        },
        'PUT',
      )
      return response.data
    } catch (error) {
      console.error('Error calling transitionAlert:', error)
      throw error
    }
  },

  /**
   * Delete a dashboard by its ID
   * @param dashboardId - The ID of the dashboard to delete
   * @returns Promise with the result of the delete operation
   */
  deleteDashboard: async (dashboardId: string): Promise<any> => {
    try {
      const url = `${ENDPOINTS.DASHBOARD_BY_ID}${dashboardId}`
      const response = await request(url, {}, 'DELETE' as any)
      return response.data
    } catch (error) {
      console.error('Error deleting dashboard:', error)
      throw error
    }
  },

  // getAIProductivityHierarchical method moved to aiMetricsService.ts

  /**
   * Fetch all team dashboards for team allocation dropdown
   * Returns array of { id, title } for dropdown display
   * Uses cache interceptor with x-enable-cache header (30 min cache)
   */
  getTeamDashboards: async (): Promise<{id: string; displayText: string}[]> => {
    try {
      const response = await get(ENDPOINTS.TEAM_DASHBOARDS_LIST, {
        headers: {'x-enable-cache': 'true'},
      })
      const dashboards = response.data || []
      return dashboards.map((dashboard: {id: string; title: string}) => ({
        id: dashboard.id,
        displayText: dashboard.title,
      }))
    } catch (error) {
      console.error('Error fetching team dashboards:', error)
      throw error
    }
  },
}

export default dashboardService
